/**
 * nested-carousel plugin
 *
 * Decorative inner carousels nested inside outer slider sections — for
 * example, one per card in `sections/v3-plans-with-cards.liquid`. The
 * wrappers are marked `aria-hidden` and accept a click to advance instead
 * of being driven by keyboard navigation (assistive tech doesn't reach
 * them).
 *
 * Three responsibilities:
 *
 *   1. Pagination override — the pagination element typically lives outside
 *      the `.swiper` element, in the parent card's content area. We discover
 *      it via the closest `[data-nested-carousel-parent="true"]` ancestor
 *      and a `[data-nested-carousel-pagination="true"]` descendant.
 *
 *   2. Click-to-advance — tapping anywhere on the swiper advances to the
 *      next slide using the section's configured `data-speed`.
 *
 *   3. Pagination bullet a11y — the whole nested carousel is marked
 *      `aria-hidden="true"` in Liquid, so nothing inside it may be
 *      focusable. Swiper's A11y module is disabled for these instances and
 *      the bullets are stripped of any focusability it would add.
 *
 * NOTE: Touch-drag behavior is left enabled (matches existing production
 * behavior). When the nested-carousel-group plugin lands, it'll layer on
 * `allowTouchMove: false` for grouped instances.
 */

const handlersBySwiper = new WeakMap();

/**
 * Find the pagination element for a nested swiper. Returns null if the
 * markup doesn't include one — caller should leave the parser-supplied
 * pagination in place rather than crash.
 */
function findExternalPagination(swiperElement) {
  const cardContainer = swiperElement.closest('[data-nested-carousel-parent="true"]');
  if (!cardContainer) return null;
  return cardContainer.querySelector('[data-nested-carousel-pagination="true"]');
}

/**
 * Strip focusability from the current pagination bullets. Idempotent.
 *
 * Takes the pagination element, not the swiper root: the bullets live in the
 * *external* pagination element, a sibling subtree of `swiper.el` under the
 * shared `[data-nested-carousel-parent]` ancestor.
 *
 * The attributes are removed rather than set to `tabindex="-1"`, which would
 * leave the bullet focusable by mouse and touch. A bare `<span>` still
 * receives clicks, which is all Swiper's clickable pagination needs.
 */
function setBulletA11y(paginationEl) {
  if (!paginationEl) return;
  const bullets = paginationEl.querySelectorAll('.swiper-pagination-bullet');
  bullets.forEach((bullet) => {
    bullet.removeAttribute('tabindex');
    bullet.removeAttribute('role');
    bullet.removeAttribute('aria-label');
    bullet.setAttribute('aria-hidden', 'true');
  });
}

export const nestedCarousel = {
  name: 'nested-carousel',

  matches(el) {
    return el.classList.contains('nested-swiper');
  },

  parseOptions(el, _section, options) {
    // The markup marks this whole subtree `aria-hidden="true"`, so nothing
    // inside it may be focusable. Swiper's A11y module — registered by
    // `swiper/bundle` and enabled for every instance in default-options.js —
    // would make clickable pagination bullets focusable (`tabindex="0"`,
    // `role="button"`, `aria-label`), label each slide, and inject a live
    // region. Plugin parseOptions runs last in the options chain, so this
    // opt-out wins.
    options.a11y = { enabled: false };

    const paginationEl = findExternalPagination(el);
    if (paginationEl) {
      options.pagination = {
        el: paginationEl,
        clickable: true,
        type: 'bullets',
      };
    }
    return options;
  },

  afterInit(swiper) {
    const el = swiper.el;
    if (!el) return;

    // Decorative wrapper — never focusable.
    el.removeAttribute('tabindex');

    const paginationEl = findExternalPagination(el);

    // Bullets exist by now: Swiper's internal `init` event fired
    // synchronously inside `new Swiper(...)` and created them.
    setBulletA11y(paginationEl);

    // `paginationRender` fires when Swiper rebuilds the bullet elements
    // (breakpoint changes, observer-driven updates); `paginationUpdate` fires
    // when it refreshes their active state.
    swiper.on('paginationRender', () => setBulletA11y(paginationEl));
    swiper.on('paginationUpdate', () => setBulletA11y(paginationEl));

    const handler = (e) => {
      e.stopPropagation();
      e.preventDefault();

      if (!swiper.enabled || typeof swiper.slideNext !== 'function') return;

      const speed = parseInt(swiper.params.speed, 10) || 2000;
      try {
        swiper.slideNext(speed);
      } catch (err) {
        console.warn('nested-carousel: slideNext failed', err);
      }
    };

    el.addEventListener('click', handler, { passive: false });
    handlersBySwiper.set(swiper, handler);
  },

  onDestroy(swiper) {
    const handler = handlersBySwiper.get(swiper);
    if (handler && swiper.el) {
      swiper.el.removeEventListener('click', handler);
    }
    handlersBySwiper.delete(swiper);
  },
};

export default nestedCarousel;
